import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import notificationAPI from '../api/notifications';
import { getWebSocketUrl } from '../services/apiUrl';
import { initLeaderElection, broadcastSocketEvent, destroyLeaderElection } from '../utils/tabLeader';
import logger from '../utils/logger';

const NotificationContext = createContext();

// ── Constants ─────────────────────────────────────────────
const MAX_NOTIFICATIONS = 200;
const FETCH_LIMIT = 50;
const RECONNECT_BASE_DELAY_MS = 1000;     // 1s initial delay
const RECONNECT_MAX_DELAY_MS = 30_000;    // 30s cap
const _RECONNECT_FACTOR = 2;               // exponential multiplier
const RECONNECT_JITTER_FACTOR = 0.5;      // ±50% randomization to prevent thundering herd
const RECONNECT_MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 60_000; // fallback polling when WS is down

// ── Socket State Machine ──────────────────────────────────
// Tracks structured state transitions for observability
const _socketState = {
  current: 'IDLE',           // IDLE | CONNECTING | CONNECTED | RECONNECTING | DISCONNECTED | FAILED
  since: Date.now(),
  transitions: [],           // last 30 transitions for diagnostics
};

const SOCKET_SM_MAX_HISTORY = 30;

const _transitionSocket = (to, meta = {}) => {
  const from = _socketState.current;
  if (from === to && !meta.force) return; // skip no-ops

  const now = Date.now();
  const durationInPrevMs = now - _socketState.since;

  _socketState.current = to;
  _socketState.since = now;

  const entry = {
    event: 'socket_transition',
    from,
    to,
    durationInPrevMs,
    timestamp: new Date(now).toISOString(),
    ...meta,
  };

  _socketState.transitions.push(entry);
  if (_socketState.transitions.length > SOCKET_SM_MAX_HISTORY) {
    _socketState.transitions = _socketState.transitions.slice(-SOCKET_SM_MAX_HISTORY);
  }

  logger.info('[Socket SM]', entry);
};

/** Get socket state machine snapshot for diagnostics */
// eslint-disable-next-line react-refresh/only-export-components
export const getSocketDiagnostics = () => ({
  current: _socketState.current,
  since: new Date(_socketState.since).toISOString(),
  uptimeMs: _socketState.current === 'CONNECTED' ? Date.now() - _socketState.since : 0,
  transitions: [..._socketState.transitions],
});

// eslint-disable-next-line react-refresh/only-export-components
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // connected | disconnected | reconnecting
  const [filter, setFilter] = useState('all'); // all | unread | type:*

  const mountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Stable user ID ref — prevents socket teardown when user object reference changes
  const userIdRef = useRef(null);
  const userId = user?.id || user?._id || null;

  // ── Fallback polling when WS is down ────────────────────
  const _startFallbackPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(() => {
      if (mountedRef.current) {
        fetchUnreadCount();
      }
    }, POLL_INTERVAL_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Browser notifications ───────────────────────────────
  const _showBrowserNotification = useCallback((notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const n = new Notification(notification.title || 'Notification', {
          body: notification.message || notification.body || '',
          icon: '/logo.png',
          tag: notification.id || notification._id,
          data: { url: notification.url || notification.link },
        });
        n.onclick = () => {
          window.focus();
          if (notification.url || notification.link) {
            window.location.href = notification.url || notification.link;
          }
          n.close();
        };
      } catch (_e) { /* safe */ }
    }
  }, []);

  // ── Handle incoming notification (shared by leader + follower) ──
  const handleNotification = useCallback((notification) => {
    if (!mountedRef.current) return;

    // Deduplicate: skip if we already have this notification
    setNotifications(prev => {
      if (prev.some(n => n.id === notification.id || n._id === notification._id)) return prev;
      const updated = [notification, ...prev];
      return updated.length > MAX_NOTIFICATIONS ? updated.slice(0, MAX_NOTIFICATIONS) : updated;
    });
    setUnreadCount(prev => prev + 1);

    // Browser notification
    _showBrowserNotification(notification);

    // Custom event for external listeners
    try {
      window.dispatchEvent(new CustomEvent('erp-notification', { detail: notification }));
    } catch (_e) { /* safe */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Connect socket (only called when this tab is the leader) ──
  const connectSocket = useCallback(() => {
    if (!userId) return;

    // Already connected for this user? Skip.
    if (socket?.connected && userIdRef.current === userId) return;

    const wsUrl = getWebSocketUrl();
    logger.info('[Socket.IO] Leader tab connecting to:', wsUrl);

    const token = localStorage.getItem('access_token');

    _transitionSocket('CONNECTING', { userId });

    const newSocket = io(wsUrl, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: true,
      withCredentials: true,
      // Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (capped)
      reconnection: true,
      reconnectionAttempts: RECONNECT_MAX_ATTEMPTS,
      reconnectionDelay: RECONNECT_BASE_DELAY_MS,
      reconnectionDelayMax: RECONNECT_MAX_DELAY_MS,
      randomizationFactor: RECONNECT_JITTER_FACTOR,
      auth: token ? { token } : undefined,
    });

    newSocket.on('connect', () => {
      _transitionSocket('CONNECTED', { socketId: newSocket.id, transport: newSocket.io?.engine?.transport?.name });
      reconnectAttemptRef.current = 0;
      if (mountedRef.current) setConnectionStatus('connected');
      newSocket.emit('join', userId);
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    });

    newSocket.on('connect_error', (err) => {
      reconnectAttemptRef.current++;
      _transitionSocket('RECONNECTING', { attempt: reconnectAttemptRef.current, error: err.message });
      if (mountedRef.current) setConnectionStatus('reconnecting');
      _startFallbackPolling();
    });

    newSocket.on('notification', (notification) => {
      handleNotification(notification);
      // Broadcast to follower tabs
      broadcastSocketEvent('notification', notification);
    });

    newSocket.on('disconnect', (reason) => {
      const nextState = reason === 'io server disconnect' || reason === 'io client disconnect'
        ? 'DISCONNECTED' : 'RECONNECTING';
      _transitionSocket(nextState, { reason });
      if (mountedRef.current) setConnectionStatus('disconnected');
      _startFallbackPolling();
    });

    newSocket.io.on('reconnect_failed', () => {
      _transitionSocket('FAILED', { totalAttempts: reconnectAttemptRef.current });
    });

    setSocket(newSocket);
    userIdRef.current = userId;
  }, [userId, socket, handleNotification, _startFallbackPolling]);

  // ── Disconnect socket (when losing leadership or unmounting) ──
  const disconnectSocket = useCallback(() => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [socket]);

  // ── WebSocket connection with cross-tab leader election ──
  useEffect(() => {
    if (!userId) {
      disconnectSocket();
      setNotifications([]);
      setUnreadCount(0);
      setConnectionStatus('disconnected');
      userIdRef.current = null;
      destroyLeaderElection();
      return;
    }

    initLeaderElection({
      onBecomeLeader: () => {
        logger.info('[TabLeader] This tab is now the leader — opening socket');
        connectSocket();
      },
      onLoseLeadership: () => {
        logger.info('[TabLeader] Lost leadership — closing socket');
        disconnectSocket();
        if (mountedRef.current) setConnectionStatus('follower');
      },
      onMessage: (msg) => {
        // Follower tab receives proxied events from leader
        if (msg?.event === 'notification') {
          handleNotification(msg.data);
        }
      },
    });

    return () => {
      disconnectSocket();
      destroyLeaderElection();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Key on stable user ID, not object reference

  // ── Fetch initial notifications ─────────────────────────
  useEffect(() => {
    if (userId) {
      fetchNotifications();
      fetchUnreadCount();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // Key on stable user ID

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationAPI.getNotifications({ limit: FETCH_LIMIT });
      if (mountedRef.current) setNotifications(data.notifications || []);
    } catch (err) {
      logger.apiError('/notifications', err);
      if (!mountedRef.current) return;
      if (err.response?.status === 401) setError('session_expired');
      else if (err.response?.status === 403) setError('permission_denied');
      else if (err.response?.status === 404) setError('not_found');
      else setError('fetch_failed');
      setNotifications([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationAPI.getUnreadCount();
      if (mountedRef.current) setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      logger.apiError('/notifications/unread-count', err);
      if (mountedRef.current) setUnreadCount(0);
    }
  }, []);

  // ── Mutation helpers ────────────────────────────────────
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(n =>
        (n.id === notificationId || n._id === notificationId)
          ? { ...n, delivery: { ...n.delivery, inApp: { ...n.delivery?.inApp, read: true } } }
          : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await notificationAPI.markAsRead(notificationId);
    } catch (err) {
      logger.apiError(`/notifications/${notificationId}/read`, err);
      // Revert on failure
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [fetchNotifications, fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    // Optimistic
    setNotifications(prev =>
      prev.map(n => ({
        ...n,
        delivery: { ...n.delivery, inApp: { ...n.delivery?.inApp, read: true } },
      }))
    );
    setUnreadCount(0);

    try {
      await notificationAPI.markAllAsRead();
    } catch (err) {
      logger.apiError('/notifications/mark-all-read', err);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [fetchNotifications, fetchUnreadCount]);

  const deleteNotification = useCallback(async (notificationId) => {
    const target = notifications.find(n => n.id === notificationId || n._id === notificationId);
    // Optimistic
    setNotifications(prev => prev.filter(n => n.id !== notificationId && n._id !== notificationId));
    if (target && !target.delivery?.inApp?.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    try {
      await notificationAPI.deleteNotification(notificationId);
    } catch (err) {
      logger.apiError(`/notifications/${notificationId}`, err);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [notifications, fetchNotifications, fetchUnreadCount]);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await notificationAPI.clearAll?.();
    } catch (err) {
      logger.apiError('/notifications/clear', err);
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [fetchNotifications, fetchUnreadCount]);

  const requestPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // ── Filtered view ───────────────────────────────────────
  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return notifications;
    if (filter === 'unread') return notifications.filter(n => !n.delivery?.inApp?.read);
    if (filter.startsWith('type:')) {
      const type = filter.slice(5);
      return notifications.filter(n => n.type === type);
    }
    return notifications;
  }, [notifications, filter]);

  // ── Grouped by date ─────────────────────────────────────
  const groupedNotifications = useMemo(() => {
    const groups = {};
    const now = new Date();
    for (const n of filteredNotifications) {
      const date = new Date(n.createdAt || n.timestamp || Date.now());
      const diffDays = Math.floor((now - date) / 86_400_000);
      let key;
      if (diffDays === 0) key = 'Today';
      else if (diffDays === 1) key = 'Yesterday';
      else if (diffDays < 7) key = 'This Week';
      else key = 'Older';
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    }
    return groups;
  }, [filteredNotifications]);

  // ── Notification types for filter UI ────────────────────
  const availableTypes = useMemo(() => {
    const types = new Set();
    for (const n of notifications) {
      if (n.type) types.add(n.type);
    }
    return Array.from(types).sort();
  }, [notifications]);

  // ── Browser permission status ───────────────────────────
  const browserPermission = useMemo(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission; // 'granted' | 'denied' | 'default'
  }, []);

  const value = {
    // Diagnostics
    socketDiagnostics: getSocketDiagnostics,

    // Data
    notifications: filteredNotifications,
    allNotifications: notifications,
    groupedNotifications,
    unreadCount,
    availableTypes,

    // State
    loading,
    error,
    connectionStatus,
    browserPermission,

    // Filters
    filter,
    setFilter,

    // Actions
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    requestPermission,
    refresh: fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
